import { generateBetItemId } from '../utils/ticketHelpers';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BetItem } from './BettingSlipDrawer';
import { getLiveEvents, getPreEvents, getEvent } from '../api/api';
import { EventSnapshot, Market, Outcome } from '../api/types';
import MatchDetailPage from './MatchDetailPage';
import { OddsButton } from './OddsButton';
import { useFavorites } from '../contexts/FavoritesContext';
import { useTranslation } from '../i18n';
import { translateMarketFilterLabel } from '../utils/marketTranslations';
import { 
  find1x2Market, 
  findRestzeitGeneric, 
  findOUGeneric, 
  findNextGoal, 
  findHandicap, 
  findBTTSGeneric, 
  findDCGeneric, 
  findH2H,
  find1stHalfMarket,
  getMarketAttr,
  isFilterAvailable,
  getMarketOptionsForSport,
  getLayoutGroupForMarketId,
  formatTippLabelWithAttr
} from '../utils/marketParsers';
import { mergeSnapshotWithPatch, applyStreamUpdates } from '../utils/streamMerge';
import { liveStreamManager, preStreamManager } from '../services/CatalogStreamManager';
import { useActiveStreams } from '../hooks/useActiveStreams';

import { SPORT_EMOJI } from './widgets/DesktopLeftSidebar';

const SPORT_NAME_TO_ID: Record<string, string> = {
  'Soccer': 'sr:sport:1',
  'Basketball': 'sr:sport:2',
  'Tennis': 'sr:sport:5',
  'Ice Hockey': 'sr:sport:4',
  'Handball': 'sr:sport:6',
  'Table Tennis': 'sr:sport:20',
  'Volleyball': 'sr:sport:23',
  'Baseball': 'sr:sport:3'
};





function countOutcomes(markets: Market[]): number {
  return markets.reduce((acc, m) => acc + m.outcomes.length, 0);
}

const LockIcon = () => (<svg className="w-4 h-4 text-gray-400 block" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>);

function formatOdds(odds: string): string {
  return parseFloat(odds).toFixed(2).replace('.', ',');
}

function formatClock(ev: EventSnapshot, t?: (k: string) => string): string {
  const sport = ev.event?.sport_name;

  const sportId = ev.event?.sport_id;

  if (sport === 'Baseball' || sportId === 'sr:sport:3') {
    const text = ev.status?.match_status_text || '';
    const numMatch = text.match(/\d+/);
    if (numMatch) {
      return `${numMatch[0]}/9`;
    }
    if (text.toLowerCase().includes('break')) {
      const ps = ev.status?.period_scores;
      if (ps && ps.length > 0) {
        return `${ps.length}/9`;
      }
      return 'Break';
    }
    if (text) return text;
  }

  const useStatusText = sport === 'Basketball' || sport === 'Tennis' || sport === 'Table Tennis' || sport === 'Volleyball' || sportId === 'sr:sport:2' || sportId === 'sr:sport:5' || sportId === 'sr:sport:20' || sportId === 'sr:sport:23';

  if (useStatusText && ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }

  const stLower = (ev.status?.match_status_text || '').toLowerCase();
  if (stLower === 'halftime' || stLower === 'halbzeit' || stLower === 'devre arası' || stLower === 'ht') {
    return t ? t('live.ht') : 'HT';
  }
  if (stLower === 'ended' || stLower === 'closed' || stLower === 'finished' || stLower === 'ft' || stLower === 'beendet' || stLower === 'bitti' || stLower === 'ms') {
    return t ? t('live.ft') : 'FT';
  }

  if (ev.status?.clock) {
    const minStr = ev.status.clock.split(':')[0];
    if (minStr.includes('+')) {
      return minStr + "'";
    }
    const minNum = parseInt(minStr, 10);
    if (!isNaN(minNum)) {
      const displayMin = minNum + 1;
      
      if (sport === 'Soccer' || sportId === 'sr:sport:1') {
        const isFirstHalf = stLower.includes('1') || stLower.includes('first') || stLower.includes('ilk');
        const isExtraTime = stLower.includes('et') || stLower.includes('extra') || stLower.includes('uzatma');

        const is90 = minStr === '90';
        const is45 = minStr === '45';

        if ((is90 && !isExtraTime && !isFirstHalf) || (is45 && isFirstHalf)) {
          const baseMin = is90 ? 90 : 45;
          // @ts-ignore: stoppage_time exists in SSE payload
          const stoppage = ev.status?.stoppage_time;
          if (stoppage && stoppage !== '0:00' && typeof stoppage === 'string') {
            const parts = stoppage.split(':');
            if (parts.length === 2) {
              const sMin = parseInt(parts[0], 10);
              const sSec = parseInt(parts[1], 10);
              if (!isNaN(sMin) && !isNaN(sSec)) {
                let added = sMin;
                if (sSec > 0 || sMin === 0) added += 1;
                return `${baseMin}+${added}'`;
              }
            }
          }
          return `${baseMin}'`;
        }

        if (isFirstHalf && displayMin > 45) {
          return `45+${displayMin - 45}'`;
        }
        if (displayMin > 90 && !isExtraTime && !isFirstHalf) {
          return `90+${displayMin - 90}'`;
        }
      }
      
      return displayMin + "'";
    }
    return minStr + "'";
  }
  if (ev.status?.match_status_text) {
    return ev.status.match_status_text.replace(/\b(?:1st|first)\b/gi, '1.').replace(/\b(?:2nd|second)\b/gi, '2.').replace(/\b(?:3rd|third)\b/gi, '3.').replace(/\b(?:4th|fourth)\b/gi, '4.').replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1.');
  }
  return 'Live';
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

interface LivePageProps {
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
  onLoginClick?: () => void;
}

const LivePage: React.FC<LivePageProps> = ({ bets, onAddBet, onRemoveBet, onLoginClick }) => {
  const navigate = useNavigate();
  const { locale, t } = useTranslation();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [liveEvents, setLiveEvents] = useState<EventSnapshot[]>([]);
  const [preEvents, setPreEvents] = useState<EventSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSport, setActiveSport] = useState('Soccer');
  const [activeBetFilter, setActiveBetFilter] = useState<string | number>('Tipp');
  const [upcomingFilter, setUpcomingFilter] = useState<string | number>('Tipp');
  const [sortBy, setSortBy] = useState<'zeit' | 'wettbewerb' | 'oddsAsc' | 'oddsDesc'>('zeit');
  const [sortModalOpen, setSortModalOpen] = useState(false);

  const handleEventClick = (ev: EventSnapshot) => {
    navigate('/match/' + ev.event_id, { state: { event: ev } });
  };

  const [startsAfter, setStartsAfter] = useState(0);
  const [startsBefore, setStartsBefore] = useState(12);
  const [loadingMore, setLoadingMore] = useState(false);
  const activeStreams = useActiveStreams();

  const loadData = useCallback(async (
    sa: number, 
    sb: number, 
    sport: string, 
    append: boolean, 
    options?: RequestInit
  ) => {
    try {
      if (append) setLoadingMore(true);
      const isFav = sport === 'Favorites';
      const sportId = isFav ? undefined : SPORT_NAME_TO_ID[sport];

      const [live, pre] = await Promise.all([
        !append ? getLiveEvents(options) : Promise.resolve({ items: [] }),
        getPreEvents({ starts_after_hours: sa, starts_before_hours: sb, sport_id: sportId }, options)
      ]);
      
      if (!append) {
        setLiveEvents(live.items);
        setPreEvents(pre.items);
      } else {
        setPreEvents(prev => {
          const newEvents = pre.items.filter(newItem => !prev.some(p => p.event_id === newItem.event_id));
          return [...prev, ...newEvents];
        });
      }
    } catch (e) {
      if ((e as any)?.name !== 'AbortError') {
        console.error('Failed to load events', e);
      }
    } finally {
      if (!options?.signal?.aborted) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    setStartsAfter(0);
    setStartsBefore(12);
    setLoading(true);
    loadData(0, 12, activeSport, false, { signal: abortController.signal });
    return () => abortController.abort();
  }, [activeSport, loadData]);

  useEffect(() => {
    let isLiveFirst = true;
    const unsubLive = liveStreamManager.subscribe((upserts, deletes) => {
      if (isLiveFirst) {
        isLiveFirst = false;
        getLiveEvents().then(res => setLiveEvents(res.items)).catch(() => {});
      } else {
        if (upserts.length > 0 || deletes.length > 0) {
          const applyUpdates = applyStreamUpdates(upserts, deletes, false);
          setLiveEvents(prev => applyUpdates(prev) || []);
        }
      }
    });

    let isPreFirst = true;
    const unsubPre = preStreamManager.subscribe((upserts, deletes) => {
      if (isPreFirst) {
        isPreFirst = false;
        getPreEvents({ starts_after_hours: 0, starts_before_hours: 48 }).then(res => setPreEvents(res.items)).catch(() => {});
      } else {
        if (upserts.length > 0 || deletes.length > 0) {
          const applyUpdates = applyStreamUpdates(upserts, deletes, false);
          setPreEvents(prev => applyUpdates(prev) || []);
        }
      }
    });

    return () => {
      unsubLive();
      unsubPre();
    };
  }, []);

  const loadMorePreEvents = useCallback(() => {
    if (loadingMore) return;
    const nextAfter = startsBefore;
    const nextBefore = startsBefore + 12;
    setStartsAfter(nextAfter);
    setStartsBefore(nextBefore);
    loadData(nextAfter, nextBefore, activeSport, true);
  }, [startsBefore, activeSport, loadData, loadingMore]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200) {
        if (!loading && !loadingMore) {
          loadMorePreEvents();
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, loadingMore, loadMorePreEvents]);

  const sportTabs = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const ev of liveEvents) {
      const s = ev.event.sport_name;
      counts[s] = (counts[s] || 0) + 1;
    }
    const SPORT_ORDER: Record<string, number> = {
      'Soccer': 1, 'Basketball': 2, 'Tennis': 3, 'Ice Hockey': 4,
      'Volleyball': 5, 'Handball': 6, 'Table Tennis': 7
    };
    return Object.entries(counts)
      .sort((a, b) => {
        const orderA = SPORT_ORDER[a[0]] ?? 999;
        const orderB = SPORT_ORDER[b[0]] ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a[0].localeCompare(b[0]);
      })
      .map(([name, count]: [string, number]) => ({ name, count, emoji: SPORT_EMOJI[name] || '🏅' }));
  }, [liveEvents]);

  useEffect(() => {
    if (sportTabs.length > 0 && activeSport !== 'Favorites') {
      const exists = sportTabs.some((t: { name: string }) => t.name === activeSport);
      if (!exists) {
        setActiveSport(sportTabs[0].name);
      }
    }
  }, [sportTabs, activeSport]);

  const favoriteLiveCount = liveEvents.filter((ev) => isFavorite(ev.event_id)).length;

  const filteredLive = activeSport === 'Favorites' 
    ? liveEvents.filter((ev) => isFavorite(ev.event_id))
    : liveEvents.filter((ev) => ev.event.sport_name === activeSport);
    
  const filteredPre = activeSport === 'Favorites'
    ? preEvents.filter((ev) => isFavorite(ev.event_id))
    : preEvents.filter((ev) => ev.event.sport_name === activeSport);

  const liveSportFilters = getMarketOptionsForSport(activeSport, true);
  const upcomingSportFilters = getMarketOptionsForSport(activeSport, false);

  const availableLiveFilters = liveSportFilters.filter((f) => filteredLive.some((ev) => isFilterAvailable(f, ev.markets)));
  const availableUpcomingFilters = upcomingSportFilters.filter((f) => filteredPre.some((ev) => isFilterAvailable(f, ev.markets)));

  const initialSortRef = useRef<Record<string, number>>({});

  const sortEvents = (events: EventSnapshot[], isLive: boolean) => {
    let sorted = [...events];
    if (sortBy === 'wettbewerb') {
      sorted.sort((a, b) => a.event.tournament_name.localeCompare(b.event.tournament_name));
    } else if (sortBy === 'oddsAsc') {
      sorted.sort((a, b) => {
        const aOdds = parseFloat(find1x2Market(a.markets || [])?.home?.odds ?? '999');
        const bOdds = parseFloat(find1x2Market(b.markets || [])?.home?.odds ?? '999');
        return aOdds - bOdds;
      });
    } else if (sortBy === 'oddsDesc') {
      sorted.sort((a, b) => {
        const aOdds = parseFloat(find1x2Market(a.markets || [])?.home?.odds ?? '0');
        const bOdds = parseFloat(find1x2Market(b.markets || [])?.home?.odds ?? '0');
        return bOdds - aOdds;
      });
    } else {
      if (isLive) {
        sorted.sort((a, b) => {
          if (initialSortRef.current[a.event_id] === undefined) {
            initialSortRef.current[a.event_id] = parseInt(a.status?.clock?.split(':')[0] || '0');
          }
          if (initialSortRef.current[b.event_id] === undefined) {
            initialSortRef.current[b.event_id] = parseInt(b.status?.clock?.split(':')[0] || '0');
          }
          return initialSortRef.current[b.event_id] - initialSortRef.current[a.event_id];
        });
      } else {
        sorted.sort((a, b) => {
          const tA = new Date(a.event.scheduled_at || a.event.start_time || 0).getTime();
          const tB = new Date(b.event.scheduled_at || b.event.start_time || 0).getTime();
          return tA - tB;
        });
      }
    }
    return sorted;
  };

  const displayedLive = sortEvents(filteredLive.filter((ev) => isFilterAvailable(activeBetFilter, ev.markets)), true).slice(0, 60);
  const displayedPre = sortEvents(filteredPre.filter((ev) => isFilterAvailable(upcomingFilter, ev.markets)), false).slice(0, 60);

  useEffect(() => {
    const liveFilters = getMarketOptionsForSport(activeSport, true);
    const available = liveFilters.filter((f) => filteredLive.some((ev) => isFilterAvailable(f, ev.markets)));
    if (available.length > 0) {
      if (!available.includes(activeBetFilter)) {
        setActiveBetFilter(available[0]);
      }
    } else if (liveFilters.length > 0 && !liveFilters.includes(activeBetFilter)) {
      setActiveBetFilter(liveFilters[0]);
    }
  }, [filteredLive, activeBetFilter, activeSport]);

  useEffect(() => {
    const upcomingFilters = getMarketOptionsForSport(activeSport, false);
    const available = upcomingFilters.filter((f) => filteredPre.some((ev) => isFilterAvailable(f, ev.markets)));
    if (available.length > 0) {
      if (!available.includes(upcomingFilter)) {
        setUpcomingFilter(available[0]);
      }
    } else if (upcomingFilters.length > 0 && !upcomingFilters.includes(upcomingFilter)) {
      setUpcomingFilter(upcomingFilters[0]);
    }
  }, [filteredPre, upcomingFilter, activeSport]);

  const isSelected = (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) =>
    bets.some((b) => b.id === generateBetItemId(eventId, marketId, outcomeId, specifiers));

  const handleOddsClick = (
    ev: EventSnapshot,
    market: Market,
    outcome: Outcome,
    tippLabel: string
  ) => {
    const id = generateBetItemId(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers);
    if (isSelected(ev.event_id, market.market_id, outcome.outcome_id, market.specifiers)) {
      onRemoveBet(id);
    } else {

      const formattedTipp = formatTippLabelWithAttr(tippLabel, market);
      onAddBet({
        id,
        matchId: 0,
        matchName: `${ev.event.home.name} - ${ev.event.away.name}`,
        betType: tippLabel === 'Heimsieg' ? 'home' : tippLabel === 'Unentschieden' ? 'draw' : 'away',
        tippLabel: formattedTipp,
        odds: formatOdds(outcome.odds),
        eventId: ev.event_id,
        marketId: market.market_id,
        outcomeId: outcome.outcome_id,
        outcomeName: outcome.name,
        specifiers: market.specifiers
          ? typeof market.specifiers === 'string'
            ? market.specifiers
            : Object.entries(market.specifiers).map(([k, v]) => `${k}=${v}`).join('|')
          : '',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen pb-20">
      {(sportTabs.length > 0 || favoriteLiveCount > 0) && (
        <div className="bg-[#f5f6f6] border-b border-gray-200 px-4 py-3 flex gap-4 overflow-x-auto">
          <button
            onClick={() => setActiveSport('Favorites')}
            className={`flex flex-col items-center gap-0.5 min-w-[48px] pb-1 border-b-2 transition ${activeSport === 'Favorites'
                ? 'border-red-600 text-gray-900'
                : 'border-transparent text-gray-500'
              }`}
          >
            <div className="relative">
              <span className="text-lg">⭐</span>
              <span className="absolute -top-1 -right-3 text-xs font-bold text-gray-700">{favoriteLiveCount}</span>
            </div>
            <span className={`text-xs whitespace-nowrap ${activeSport === 'Favorites' ? 'font-bold' : 'font-medium'}`}>Favoriler</span>
          </button>
          {sportTabs.map((sport: { name: string; count: number; emoji: string }) => (
            <button
              key={sport.name}
              onClick={() => setActiveSport(sport.name)}
              className={`flex flex-col items-center gap-0.5 min-w-[48px] pb-1 border-b-2 transition ${activeSport === sport.name
                  ? 'border-red-600 text-gray-900'
                  : 'border-transparent text-gray-500'
                }`}
            >
              <div className="relative">
                <span className="text-lg">{sport.emoji}</span>
                <span className="absolute -top-1 -right-3 text-xs font-bold text-gray-700">{sport.count}</span>
              </div>
              <span className={`text-xs whitespace-nowrap ${activeSport === sport.name ? 'font-bold' : 'font-medium'}`}>{sport.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="px-3 pt-3">
        <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
          <button 
            onClick={() => setSortModalOpen(true)}
            className="flex-shrink-0 flex items-center justify-center w-9 h-9 border border-gray-300 rounded-full bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition" 
            title="Sortiere nach"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8l4-4 4 4M7 4v16" />
              <path d="M21 16l-4 4-4-4M17 20V4" />
            </svg>
          </button>
          {availableLiveFilters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveBetFilter(filter)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${activeBetFilter === filter
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-700 border-gray-300'
                }`}
            >
              {translateMarketFilterLabel(filter, locale)}
            </button>
          ))}
        </div>

        {displayedLive.length === 0 ? (
          <div className="bg-white rounded-xl p-6 text-center text-gray-400 text-sm mb-4">
            {filteredLive.length === 0 ? "Bu spor için canlı maç yok." : "Bu market için canlı maç yok."}
          </div>
        ) : (
          <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-4">
            {displayedLive.map((ev, idx) => {
              const odds3way = find1x2Market(ev.markets);
              const total = countOutcomes(ev.markets);
              const redHome = (ev.status?.statistics?.red_cards?.home || 0) > 0;
              const redAway = (ev.status?.statistics?.red_cards?.away || 0) > 0;

              return (
                <div key={ev.event_id}>
                  {idx > 0 && <div className="border-t border-gray-100 mx-4" />}
                  <div className="px-4 py-3">
                    <div className="flex gap-3 mb-3 cursor-pointer" onClick={() => handleEventClick(ev)}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(ev.event_id);
                        }}
                        className="text-gray-300 hover:text-yellow-400 pt-0.5"
                      >
                        <svg className={`w-5 h-5 ${isFavorite(ev.event_id) ? 'text-yellow-400 fill-current' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                      <div className="text-left min-w-[45px] flex-shrink-0 pt-0.5">
                        <div className="text-[14px] font-bold text-[#1a1a1a] leading-none">{formatClock(ev, t)}</div>
                        <div className="w-[14px] h-[2.5px] bg-[#d41534] rounded-full mt-1.5 ml-0.5"></div>
                      </div>
                      <div className="flex-1 min-w-0 flex items-center">
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                          <div className="flex items-center gap-1.5 truncate h-[24px]">
                            <span className="text-[15px] font-semibold text-[#1a1a1a] truncate">{ev.event.home.name}</span>
                            {redHome && <span className="w-3 h-4 bg-[#d41534] rounded-sm inline-block" />}
                          </div>
                          <div className="flex items-center gap-1.5 truncate h-[24px]">
                            <span className="text-[15px] font-semibold text-[#1a1a1a] truncate">{ev.event.away.name}</span>
                            {redAway && <span className="w-3 h-4 bg-[#d41534] rounded-sm inline-block" />}
                          </div>
                        </div>

                        {activeStreams.has(ev.event_id) && (
                          <div className="mx-3 flex-shrink-0">
                            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}

                        <div className="flex flex-col justify-center flex-shrink-0">
                          <div className="flex items-center gap-2.5 ml-2 h-[24px]">
                            <span className="text-[14px] font-medium text-gray-400">{ev.status?.period_scores?.[0]?.home_score ?? '0'}</span>
                            <span className="text-[15px] font-bold text-[#1a1a1a] w-3 text-right">{ev.status?.home_score ?? '-'}</span>
                          </div>
                          <div className="flex items-center gap-2.5 ml-2 h-[24px]">
                            <span className="text-[14px] font-medium text-gray-400">{ev.status?.period_scores?.[0]?.away_score ?? '0'}</span>
                            <span className="text-[15px] font-bold text-[#1a1a1a] w-3 text-right">{ev.status?.away_score ?? '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <div className="flex gap-2 items-center">
                        {(() => {
                          const layoutGrp = getLayoutGroupForMarketId(activeBetFilter);
                          let attrMarket: Market | undefined;
                          if (layoutGrp === 'Tipp') { const r = find1x2Market(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Tipp Restzeit') { const r = findRestzeitGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Über/Unter') { const r = findOUGeneric(ev.markets, false, ev); attrMarket = r?.market; }
                          else if (layoutGrp === 'Nächstes Tor') { const r = findNextGoal(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') { const r = findHandicap(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Beide Teams treffen') { const r = findBTTSGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Halbzeit-Über/Unter') { const r = findOUGeneric(ev.markets, true, ev); attrMarket = r?.market; }
                          else if (layoutGrp === 'Doppelte Chance') { const r = findDCGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') { const r = findH2H(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === '1. Halbzeit') { const r = find1stHalfMarket(ev.markets); attrMarket = r?.market; }
                          if (!attrMarket) return null;
                          const attr = getMarketAttr(attrMarket);
                          if (!attr) return null;
                          return <span className="text-[11px] font-bold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{attr}</span>;
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === 'Tipp' && (() => {
                          const odds3way = find1x2Market(ev.markets);
                          if (!odds3way) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: odds3way.home, tipp: 'Heimsieg' },
                              { label: 'X', outcome: odds3way.draw, tipp: 'Unentschieden' },
                              { label: '2', outcome: odds3way.away, tipp: 'Auswärtssieg' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, odds3way.market.market_id, outcome.outcome_id, odds3way.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, odds3way.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded-lg px-2.5 py-2 transition ${sel ? 'bg-[#1a1a1a]' : 'bg-[#ebedf0] active:bg-[#e2e5e8]'
                                    }`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === 'Tipp Restzeit' && (() => {
                          const rz = findRestzeitGeneric(ev.markets);
                          if (!rz) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: rz.home, tipp: 'Restzeit Heimsieg' },
                              { label: 'X', outcome: rz.draw, tipp: 'Restzeit Unentschieden' },
                              { label: '2', outcome: rz.away, tipp: 'Restzeit Auswärtssieg' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, rz.market.market_id, outcome.outcome_id, rz.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, rz.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === 'Über/Unter' && (() => {
                          const ou = findOUGeneric(ev.markets, false, ev);
                          if (!ou) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '+', outcome: ou.over, tipp: 'Über 0,5' },
                              { label: '−', outcome: ou.under, tipp: 'Unter 0,5' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, ou.market.market_id, outcome.outcome_id, ou.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, ou.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === 'Nächstes Tor' && (() => {
                          const ng = findNextGoal(ev.markets);
                          if (!ng) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: ng.home, tipp: 'Nächstes Tor Heim' },
                              { label: 'X', outcome: ng.none, tipp: 'Kein Tor' },
                              { label: '2', outcome: ng.away, tipp: 'Nächstes Tor Auswärts' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, ng.market.market_id, outcome.outcome_id, ng.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, ng.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {(getLayoutGroupForMarketId(activeBetFilter) === 'Handicap' || getLayoutGroupForMarketId(activeBetFilter) === 'Satz-Handicap') && (() => {
                          const hc = findHandicap(ev.markets);
                          if (!hc) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          const items: { label: string; outcome: Outcome; tipp: string }[] = [
                            { label: '1', outcome: hc.o1, tipp: getLayoutGroupForMarketId(activeBetFilter) === 'Satz-Handicap' ? 'Satz-Handicap 1' : 'Handicap 1' },
                          ];
                          if (hc.o3) items.push({ label: 'X', outcome: hc.o3, tipp: 'Handicap X' });
                          items.push({ label: '2', outcome: hc.o2, tipp: getLayoutGroupForMarketId(activeBetFilter) === 'Satz-Handicap' ? 'Satz-Handicap 2' : 'Handicap 2' });
                          return items.map(({ label, outcome, tipp }) => {
                            const sel = isSelected(ev.event_id, hc.market.market_id, outcome.outcome_id, hc.market.specifiers);
                            return (
                              <OddsButton odds={outcome.odds} selected={sel || false}
                                key={label}
                                onClick={() => handleOddsClick(ev, hc.market, outcome, tipp)}
                                className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                              >
                                <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                              </OddsButton>
                            );
                          });
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === 'Beide Teams treffen' && (() => {
                          const btts = findBTTSGeneric(ev.markets);
                          if (!btts) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: 'Ja', outcome: btts.yes, tipp: 'Ja' },
                              { label: 'Nein', outcome: btts.no, tipp: 'Nein' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, btts.market.market_id, outcome.outcome_id, btts.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, btts.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === 'Halbzeit-Über/Unter' && (() => {
                          const ou = findOUGeneric(ev.markets, true, ev);
                          if (!ou) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '+', outcome: ou.over, tipp: '1.Hz Über 0,5' },
                              { label: '−', outcome: ou.under, tipp: '1.Hz Unter 0,5' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, ou.market.market_id, outcome.outcome_id, ou.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, ou.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === 'Doppelte Chance' && (() => {
                          const dc = findDCGeneric(ev.markets);
                          if (!dc) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1X', outcome: dc.o1x, tipp: '1X' },
                              { label: '12', outcome: dc.o12, tipp: '12' },
                              { label: 'X2', outcome: dc.ox2, tipp: 'X2' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, dc.market.market_id, outcome.outcome_id, dc.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, dc.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {(getLayoutGroupForMarketId(activeBetFilter) === 'Head-To-Head' || getLayoutGroupForMarketId(activeBetFilter) === 'Sieger' || getLayoutGroupForMarketId(activeBetFilter) === 'Draw No Bet') && (() => {
                          const h2h = findH2H(ev.markets);
                          if (!h2h) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          const tippLabelHome = getLayoutGroupForMarketId(activeBetFilter) === 'Sieger' ? 'Sieger Heim' : getLayoutGroupForMarketId(activeBetFilter) === 'Draw No Bet' ? 'Draw No Bet Heim' : 'H2H Heim';
                          const tippLabelAway = getLayoutGroupForMarketId(activeBetFilter) === 'Sieger' ? 'Sieger Auswärts' : getLayoutGroupForMarketId(activeBetFilter) === 'Draw No Bet' ? 'Draw No Bet Auswärts' : 'H2H Auswärts';
                          return (
                            ([
                              { label: '1', outcome: h2h.home, tipp: tippLabelHome },
                              { label: '2', outcome: h2h.away, tipp: tippLabelAway },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, h2h.market.market_id, outcome.outcome_id, h2h.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, h2h.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(activeBetFilter) === '1. Halbzeit' && (() => {
                          const m = find1stHalfMarket(ev.markets);
                          if (!m) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: m.home, tipp: '1.Hz Heimsieg' },
                              { label: 'X', outcome: m.draw, tipp: '1.Hz Unentschieden' },
                              { label: '2', outcome: m.away, tipp: '1.Hz Auswärtssieg' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, m.market.market_id, outcome.outcome_id, m.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, m.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-[12px] font-medium truncate ${sel ? 'text-[#eee]' : 'text-[#555]'}`}>{label}</span>
                                  <span className={`text-[14px] font-bold ${sel ? 'text-white' : 'text-gray-900'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        <button className="flex items-center gap-0.5 text-[12px] font-medium text-[#777777] whitespace-nowrap ml-1.5 pt-0.5">
                          {total} <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </button>
                      </div>


                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filteredPre.length > 0 && (
          <>
            <h2 className="text-[15px] font-bold text-gray-900 mb-3">{t('section.next6Hours') || 'Nächste 6 Stunden'}</h2>
            <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
              {availableUpcomingFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setUpcomingFilter(f)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${upcomingFilter === f
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-700 border-gray-300'
                    }`}
                >
                  {translateMarketFilterLabel(f, locale)}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              {displayedPre.map((ev) => {
                const odds3way = find1x2Market(ev.markets);
                const total = countOutcomes(ev.markets);
                return (
                  <div key={ev.event_id} className="bg-white rounded-xl shadow-sm px-4 py-3">
                    <div className="flex gap-3 items-start mb-2 cursor-pointer" onClick={() => handleEventClick(ev)}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(ev.event_id);
                        }}
                        className="text-gray-300 hover:text-yellow-400 mt-0.5"
                      >
                        <svg className={`w-5 h-5 ${isFavorite(ev.event_id) ? 'text-yellow-400 fill-current' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>
                      <div className="text-left min-w-[40px]">
                        <div className="text-xs text-gray-500">Heute</div>
                        <div className="text-xs font-bold text-gray-700">{formatTime(ev.event.scheduled_at)}</div>
                      </div>
                      <div className="flex-1">
                        <p className="text-[15px] font-medium text-gray-800 truncate">{ev.event.home.name}</p>
                        <p className="text-[15px] font-medium text-gray-800 truncate">{ev.event.away.name}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex gap-2 items-center">
                        {(() => {
                          const layoutGrp = getLayoutGroupForMarketId(upcomingFilter);
                          let attrMarket: Market | undefined;
                          if (layoutGrp === 'Tipp') { const r = find1x2Market(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Tipp Restzeit') { const r = findRestzeitGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Über/Unter') { const r = findOUGeneric(ev.markets, false, ev); attrMarket = r?.market; }
                          else if (layoutGrp === 'Nächstes Tor') { const r = findNextGoal(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Handicap' || layoutGrp === 'Satz-Handicap') { const r = findHandicap(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Beide Teams treffen') { const r = findBTTSGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Halbzeit-Über/Unter') { const r = findOUGeneric(ev.markets, true, ev); attrMarket = r?.market; }
                          else if (layoutGrp === 'Doppelte Chance') { const r = findDCGeneric(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === 'Head-To-Head' || layoutGrp === 'Sieger' || layoutGrp === 'Draw No Bet') { const r = findH2H(ev.markets); attrMarket = r?.market; }
                          else if (layoutGrp === '1. Halbzeit') { const r = find1stHalfMarket(ev.markets); attrMarket = r?.market; }
                          if (!attrMarket) return null;
                          const attr = getMarketAttr(attrMarket);
                          if (!attr) return null;
                          return <span className="text-[11px] font-bold text-gray-900 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{attr}</span>;
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === 'Tipp' && (() => {
                          const odds3way = find1x2Market(ev.markets);
                          if (!odds3way) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: odds3way.home, tipp: 'Heimsieg' },
                              { label: 'X', outcome: odds3way.draw, tipp: 'Unentschieden' },
                              { label: '2', outcome: odds3way.away, tipp: 'Auswärtssieg' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, odds3way.market.market_id, outcome.outcome_id, odds3way.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, odds3way.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded-lg px-3 py-2 transition ${sel ? 'bg-gray-800' : 'bg-[#e7eaec] hover:bg-[#d8dce0]'
                                    }`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === 'Tipp Restzeit' && (() => {
                          const rz = findRestzeitGeneric(ev.markets);
                          if (!rz) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: rz.home, tipp: 'Restzeit Heimsieg' },
                              { label: 'X', outcome: rz.draw, tipp: 'Restzeit Unentschieden' },
                              { label: '2', outcome: rz.away, tipp: 'Restzeit Auswärtssieg' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, rz.market.market_id, outcome.outcome_id, rz.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, rz.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === 'Über/Unter' && (() => {
                          const ou = findOUGeneric(ev.markets, false, ev);
                          if (!ou) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '+', outcome: ou.over, tipp: 'Über 0,5' },
                              { label: '−', outcome: ou.under, tipp: 'Unter 0,5' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, ou.market.market_id, outcome.outcome_id, ou.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, ou.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === 'Nächstes Tor' && (() => {
                          const ng = findNextGoal(ev.markets);
                          if (!ng) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: ng.home, tipp: 'Nächstes Tor Heim' },
                              { label: 'X', outcome: ng.none, tipp: 'Kein Tor' },
                              { label: '2', outcome: ng.away, tipp: 'Nächstes Tor Auswärts' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, ng.market.market_id, outcome.outcome_id, ng.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, ng.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {(getLayoutGroupForMarketId(upcomingFilter) === 'Handicap' || getLayoutGroupForMarketId(upcomingFilter) === 'Satz-Handicap') && (() => {
                          const hc = findHandicap(ev.markets);
                          if (!hc) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          const items: { label: string; outcome: Outcome; tipp: string }[] = [
                            { label: '1', outcome: hc.o1, tipp: getLayoutGroupForMarketId(upcomingFilter) === 'Satz-Handicap' ? 'Satz-Handicap 1' : 'Handicap 1' },
                          ];
                          if (hc.o3) items.push({ label: 'X', outcome: hc.o3, tipp: 'Handicap X' });
                          items.push({ label: '2', outcome: hc.o2, tipp: getLayoutGroupForMarketId(upcomingFilter) === 'Satz-Handicap' ? 'Satz-Handicap 2' : 'Handicap 2' });
                          return items.map(({ label, outcome, tipp }) => {
                            const sel = isSelected(ev.event_id, hc.market.market_id, outcome.outcome_id, hc.market.specifiers);
                            return (
                              <OddsButton odds={outcome.odds} selected={sel || false}
                                key={label}
                                onClick={() => handleOddsClick(ev, hc.market, outcome, tipp)}
                                className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                              >
                                <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                              </OddsButton>
                            );
                          });
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === 'Beide Teams treffen' && (() => {
                          const btts = findBTTSGeneric(ev.markets);
                          if (!btts) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: 'Ja', outcome: btts.yes, tipp: 'Ja' },
                              { label: 'Nein', outcome: btts.no, tipp: 'Nein' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, btts.market.market_id, outcome.outcome_id, btts.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, btts.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === 'Halbzeit-Über/Unter' && (() => {
                          const ou = findOUGeneric(ev.markets, true, ev);
                          if (!ou) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '+', outcome: ou.over, tipp: '1.Hz Über 0,5' },
                              { label: '−', outcome: ou.under, tipp: '1.Hz Unter 0,5' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, ou.market.market_id, outcome.outcome_id, ou.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, ou.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === 'Doppelte Chance' && (() => {
                          const dc = findDCGeneric(ev.markets);
                          if (!dc) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1X', outcome: dc.o1x, tipp: '1X' },
                              { label: '12', outcome: dc.o12, tipp: '12' },
                              { label: 'X2', outcome: dc.ox2, tipp: 'X2' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, dc.market.market_id, outcome.outcome_id, dc.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, dc.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {(getLayoutGroupForMarketId(upcomingFilter) === 'Head-To-Head' || getLayoutGroupForMarketId(upcomingFilter) === 'Sieger' || getLayoutGroupForMarketId(upcomingFilter) === 'Draw No Bet') && (() => {
                          const h2h = findH2H(ev.markets);
                          if (!h2h) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          const tippLabelHome = getLayoutGroupForMarketId(upcomingFilter) === 'Sieger' ? 'Sieger Heim' : getLayoutGroupForMarketId(upcomingFilter) === 'Draw No Bet' ? 'Draw No Bet Heim' : 'H2H Heim';
                          const tippLabelAway = getLayoutGroupForMarketId(upcomingFilter) === 'Sieger' ? 'Sieger Auswärts' : getLayoutGroupForMarketId(upcomingFilter) === 'Draw No Bet' ? 'Draw No Bet Auswärts' : 'H2H Auswärts';
                          return (
                            ([
                              { label: '1', outcome: h2h.home, tipp: tippLabelHome },
                              { label: '2', outcome: h2h.away, tipp: tippLabelAway },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, h2h.market.market_id, outcome.outcome_id, h2h.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, h2h.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        {getLayoutGroupForMarketId(upcomingFilter) === '1. Halbzeit' && (() => {
                          const m = find1stHalfMarket(ev.markets);
                          if (!m) return <div className="flex-1 text-xs text-gray-400">Oranlar yok</div>;
                          return (
                            ([
                              { label: '1', outcome: m.home, tipp: '1.Hz Heimsieg' },
                              { label: 'X', outcome: m.draw, tipp: '1.Hz Unentschieden' },
                              { label: '2', outcome: m.away, tipp: '1.Hz Auswärtssieg' },
                            ] as const).map(({ label, outcome, tipp }) => {
                              const sel = isSelected(ev.event_id, m.market.market_id, outcome.outcome_id, m.market.specifiers);
                              return (
                                <OddsButton odds={outcome.odds} selected={sel || false}
                                  key={label}
                                  onClick={() => handleOddsClick(ev, m.market, outcome, tipp)}
                                  className={`flex-1 flex items-center justify-between rounded px-2 py-1.5 transition min-w-0 border ${sel ? 'bg-[#333] border-[#333]' : (!outcome.active || formatOdds(outcome.odds) === '-') ? 'bg-gray-50 opacity-40 cursor-not-allowed border-gray-200' : 'bg-[#e3e6eb] border-[#c0c5cc] hover:bg-[#d8dce0]'}`}
                                >
                                  <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{label}</span>
                                  <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{(!outcome.active || formatOdds(outcome.odds) === '-') ? <LockIcon /> : formatOdds(outcome.odds)}</span>
                                </OddsButton>
                              );
                            })
                          );
                        })()}
                        <button className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap ml-1">
                          {total} <span>›</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {loadingMore && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
                <span className="ml-2 text-sm text-gray-500 font-medium">Lade weitere Ereignisse...</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sort Modal */}
      {sortModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center" onClick={() => setSortModalOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-md bg-white rounded-t-2xl shadow-2xl pointer-events-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="grid grid-cols-[auto_1fr_auto] items-center min-h-14 px-4 border-b border-gray-100">
              <div />
              <div className="text-center text-[17px] font-semibold text-gray-900">Sortiere nach:</div>
              <button onClick={() => setSortModalOpen(false)} className="text-gray-400 hover:text-gray-700 p-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <ul className="py-2">
              {([
                { key: 'zeit', label: 'Zeit' },
                { key: 'wettbewerb', label: 'Wettbewerb' },
                { key: 'oddsAsc', label: 'Quoten: Aufsteigend' },
                { key: 'oddsDesc', label: 'Quoten: Absteigend' },
              ] as const).map(({ key, label }) => (
                <li
                  key={key}
                  onClick={() => { setSortBy(key); setSortModalOpen(false); }}
                  className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition"
                >
                  <span className={`text-[16px] ${sortBy === key ? 'font-semibold text-gray-900' : 'font-normal text-gray-700'}`}>{label}</span>
                  {sortBy === key && (
                    <svg className="w-5 h-5 text-gray-900" viewBox="0 0 19 16" fill="currentColor">
                      <path d="M17.1573 0.435014C16.9627 0.679709 14.5718 3.69042 11.8443 7.12549C9.11677 10.5605 6.87729 13.3683 6.86769 13.3649C6.85812 13.3615 5.51622 12.0145 3.88574 10.3714L0.921208 7.38412L0.519965 7.77059C0.299288 7.98314 0.0920085 8.18521 0.0593511 8.21963L0 8.28218L2.61212 10.9124C4.04878 12.359 5.61959 13.9406 6.10279 14.427L6.98135 15.3115L12.7407 8.0584C15.9083 4.0692 18.5 0.797431 18.5 0.787812C18.5 0.774251 17.598 0.0438195 17.5273 0.000112908C17.5184 -0.00538105 17.3519 0.190318 17.1573 0.435014Z" />
                    </svg>
                  )}
                </li>
              ))}
            </ul>
            <div className="px-5 pb-5 pt-2">
              <button
                onClick={() => setSortModalOpen(false)}
                className="w-full h-12 bg-[#cc0000] hover:bg-[#aa0000] text-white text-[16px] font-semibold rounded-lg transition"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LivePage;
